import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  setDoc,
  writeBatch,
  getDoc
} from "firebase/firestore";
import { db, LEGACY_TENANT_ID } from "@/lib/firebase";
import { logger } from "@/lib/logger";

// Invitation interface
export interface Invitation {
  id: string;
  inviterUserId: string;      // Praktikant der eingeladen hat
  inviteeName: string;        // Name des Eingeladenen
  inviteeEmail: string;       // E-Mail des Eingeladenen
  inviteePosition: string;    // Position (praktikant, trainee, berater)
  status: 'pending' | 'activated' | 'rejected';
  pointsAwarded: boolean;     // Ob Punkte bereits vergeben wurden
  pointsAmount: number;       // Anzahl der vergebenen Punkte
  createdAt: Date;
  activatedAt?: Date;
  createdByUserId?: string;   // User ID des erstellten Kontos (nach Aktivierung)
  rejectedAt?: Date;
  rejectedReason?: string;
  tenantId?: string;
}

// Interface for creating new invitations
export interface CreateInvitationData {
  inviterUserId: string;
  inviteeName: string;
  inviteeEmail: string;
  inviteePosition: string;
  tenantId?: string;
}

const INVITATIONS_COLLECTION = "invitations";

/**
 * Creates a new invitation
 */
export async function createInvitation(data: CreateInvitationData): Promise<string> {
  try {
    // Check if email is already invited or exists as user
    const existingInvitation = await getInvitationByEmail(data.inviteeEmail);
    if (existingInvitation) {
      throw new Error("Diese E-Mail-Adresse wurde bereits eingeladen.");
    }

    // Calculate points based on position
    const pointsAmount = calculateInvitationPoints(data.inviteePosition);

    const invitation: Omit<Invitation, 'id'> = {
      inviterUserId: data.inviterUserId,
      inviteeName: data.inviteeName,
      inviteeEmail: data.inviteeEmail.toLowerCase(),
      inviteePosition: data.inviteePosition,
      status: 'pending',
      pointsAwarded: false,
      pointsAmount,
      createdAt: new Date(),
      tenantId: data.tenantId || LEGACY_TENANT_ID,
    };

    const docRef = await addDoc(collection(db, INVITATIONS_COLLECTION), invitation);
    logger.log("Created invitation:", docRef.id, invitation);

    return docRef.id;
  } catch (error) {
    logger.error("Error creating invitation:", error);
    throw error;
  }
}

/**
 * Gets all invitations for a specific inviter
 */
export async function getInvitationsByInviter(inviterUserId: string): Promise<Invitation[]> {
  try {
    const q = query(
      collection(db, INVITATIONS_COLLECTION),
      where("inviterUserId", "==", inviterUserId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const invitations: Invitation[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data() as any;
      invitations.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        activatedAt: data.activatedAt?.toDate(),
        rejectedAt: data.rejectedAt?.toDate(),
        tenantId: data.tenantId || LEGACY_TENANT_ID,
      } as Invitation);
    });

    return invitations;
  } catch (error) {
    logger.error("Error fetching invitations by inviter:", error);
    throw error;
  }
}

/**
 * Gets all pending invitations (for admin view)
 */
export async function getPendingInvitations(tenantId: string = LEGACY_TENANT_ID): Promise<Invitation[]> {
  try {
    const invitationsRef = collection(db, INVITATIONS_COLLECTION);
    let q;

    if (tenantId === LEGACY_TENANT_ID) {
      q = query(
        invitationsRef,
        where("status", "==", "pending"),
        orderBy("createdAt", "desc")
      );
    } else {
      q = query(
        invitationsRef,
        where("status", "==", "pending"),
        where("tenantId", "==", tenantId),
        orderBy("createdAt", "desc")
      );
    }

    const querySnapshot = await getDocs(q);
    const invitations: Invitation[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data() as any;
      // Filter legacy if needed
      if (tenantId === LEGACY_TENANT_ID && data.tenantId && data.tenantId !== LEGACY_TENANT_ID) return;

      invitations.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        activatedAt: data.activatedAt?.toDate(),
        rejectedAt: data.rejectedAt?.toDate(),
        tenantId: data.tenantId || LEGACY_TENANT_ID,
      } as Invitation);
    });

    return invitations;
  } catch (error) {
    logger.error("Error fetching pending invitations:", error);
    throw error;
  }
}

/**
 * Gets an invitation by email
 */
export async function getInvitationByEmail(email: string): Promise<Invitation | null> {
  try {
    const q = query(
      collection(db, INVITATIONS_COLLECTION),
      where("inviteeEmail", "==", email.toLowerCase())
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data() as any;

    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      activatedAt: data.activatedAt?.toDate(),
      rejectedAt: data.rejectedAt?.toDate(),
      tenantId: data.tenantId || LEGACY_TENANT_ID,
    } as Invitation;
  } catch (error) {
    logger.error("Error fetching invitation by email:", error);
    throw error;
  }
}

/**
 * Gets an invitation by ID
 */
export async function getInvitation(id: string): Promise<Invitation | null> {
  try {
    const docRef = doc(db, INVITATIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data() as any;

    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      activatedAt: data.activatedAt?.toDate(),
      rejectedAt: data.rejectedAt?.toDate(),
      tenantId: data.tenantId || LEGACY_TENANT_ID,
    } as Invitation;
  } catch (error) {
    logger.error("Error fetching invitation by ID:", error);
    throw error;
  }
}

/**
 * Activates an invitation by creating a user account and awarding points
 */
export async function activateInvitation(
  invitationId: string,
  createdUserId: string,
  adminUserId: string
): Promise<boolean> {
  try {
    const batch = writeBatch(db);

    // Update invitation status
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);
    batch.update(invitationRef, {
      status: 'activated',
      activatedAt: new Date(),
      createdByUserId: createdUserId
    });

    // Mark points as awarded (points will be awarded by the calling function)
    batch.update(invitationRef, {
      pointsAwarded: true
    });

    await batch.commit();

    logger.log("Successfully activated invitation:", invitationId);
    return true;
  } catch (error) {
    logger.error("Error activating invitation:", error);
    return false;
  }
}

/**
 * Rejects an invitation
 */
export async function rejectInvitation(
  invitationId: string,
  reason?: string
): Promise<boolean> {
  try {
    const invitationRef = doc(db, INVITATIONS_COLLECTION, invitationId);

    await updateDoc(invitationRef, {
      status: 'rejected',
      rejectedAt: new Date(),
      rejectedReason: reason || "Von Administrator abgelehnt"
    });

    logger.log("Successfully rejected invitation:", invitationId);
    return true;
  } catch (error) {
    logger.error("Error rejecting invitation:", error);
    return false;
  }
}

/**
 * Calculates points based on invited user position
 */
export function calculateInvitationPoints(position: string): number {
  switch (position) {
    case 'praktikant':
    case 'dual-student':
      return 250;
    case 'berater':
    case 'trainee':
    case 'beraterassistent':
      return 1000;
    default:
      return 250; // Default fallback
  }
}

/**
 * Gets all invitations (admin only)
 */
export async function getAllInvitations(tenantId: string = LEGACY_TENANT_ID): Promise<Invitation[]> {
  try {
    const invitationsRef = collection(db, INVITATIONS_COLLECTION);
    let q;

    if (tenantId === LEGACY_TENANT_ID) {
      q = query(
        invitationsRef,
        orderBy("createdAt", "desc")
      );
    } else {
      q = query(
        invitationsRef,
        where("tenantId", "==", tenantId),
        orderBy("createdAt", "desc")
      );
    }

    const querySnapshot = await getDocs(q);
    const invitations: Invitation[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter legacy if needed
      if (tenantId === LEGACY_TENANT_ID && data.tenantId && data.tenantId !== LEGACY_TENANT_ID) return;

      invitations.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        activatedAt: data.activatedAt?.toDate(),
        rejectedAt: data.rejectedAt?.toDate(),
        tenantId: data.tenantId || LEGACY_TENANT_ID,
      } as Invitation);
    });

    return invitations;
  } catch (error) {
    logger.error("Error fetching all invitations:", error);
    throw error;
  }
}